/**
 * Licenza di valutazione (ticket 6ab54367) — stato letto dal backend.
 *
 * Il backend espone `GET /api/deployment/licence` senza autenticazione; qui si
 * legge prima del login per mostrare il banner nella pagina di accesso.
 *
 * Qualunque risposta che non sia un oggetto con `evaluation: true` vale come
 * "nessuna valutazione": un backend piu' vecchio senza la rotta (404), un
 * backend irraggiungibile o un index.html servito al posto del JSON non devono
 * mai bloccare l'accesso. A bloccare davvero e' il backend (403 sul login);
 * il portale si limita a spiegarlo.
 */
import $api from '../http'

export const NO_EVALUATION = Object.freeze({
  evaluation: false,
  valid_until: null,
  expired: false,
  days_left: null,
  reason: null,
})

// Soglia sotto la quale il banner mostra anche i giorni rimasti.
export const DAYS_LEFT_WARNING = 14

export function normaliseLicence(data) {
  if (!data || typeof data !== 'object' || data.evaluation !== true) {
    return NO_EVALUATION
  }
  return {
    evaluation: true,
    valid_until: typeof data.valid_until === 'string' ? data.valid_until : null,
    expired: data.expired === true,
    days_left: Number.isFinite(data.days_left) ? data.days_left : null,
    reason: typeof data.reason === 'string' ? data.reason : null,
  }
}

// Il 403 del backend a valutazione scaduta porta con se' lo stato:
// {reason: "evaluation_expired", valid_until, licence_reason}. Serve quando il
// banner non l'aveva ancora (backend scaduto dopo il caricamento della pagina).
export function licenceFromExpiredDetail(detail) {
  if (!detail || typeof detail !== 'object' || detail.reason !== 'evaluation_expired') {
    return null
  }
  return {
    evaluation: true,
    valid_until: typeof detail.valid_until === 'string' ? detail.valid_until : null,
    expired: true,
    days_left: 0,
    reason:
      typeof detail.licence_reason === 'string' ? detail.licence_reason : 'expired',
  }
}

export async function fetchLicence(client = $api) {
  try {
    // Timeout corto: l'istanza condivisa aspetta fino a 9 minuti, e il banner
    // non deve restare in sospeso per un backend che non risponde.
    const response = await client.get('/deployment/licence', { timeout: 10000 })
    return normaliseLicence(response?.data)
  } catch (e) {
    return NO_EVALUATION
  }
}

/**
 * Da stato a banner. `kind`:
 *   none      — nessuna valutazione, niente banner
 *   active    — valutazione in corso
 *   expiring  — in corso, ≤ DAYS_LEFT_WARNING giorni rimasti
 *   expired   — scaduta: modulo disabilitato
 *   invalid   — file assente o non valido: modulo disabilitato
 */
export function licenceBanner(status) {
  if (!status || status.evaluation !== true) {
    return { kind: 'none', blocked: false }
  }
  if (status.reason === 'missing' || status.reason === 'invalid') {
    return { kind: 'invalid', blocked: true }
  }
  if (status.expired) {
    return { kind: 'expired', blocked: true }
  }
  if (status.days_left !== null && status.days_left <= DAYS_LEFT_WARNING) {
    return { kind: 'expiring', blocked: false }
  }
  return { kind: 'active', blocked: false }
}

// La scadenza e' emessa come fine giornata UTC (23:59:59Z): formattata nel
// fuso del browser, in Europa diventerebbe il giorno dopo. Si mostra la data
// UTC, cioe' quella scritta nella licenza.
export function formatLicenceDate(iso, locale) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      dateStyle: 'long',
      timeZone: 'UTC',
    }).format(date)
  } catch (e) {
    return date.toISOString().slice(0, 10)
  }
}
